import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Basictabs from "components/common/Tabs";
import Typography from "components/common/Typography";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { fetchPropertyInfo, reset as resetAbovePropertySlice } from "../AboveProperty/AboveProperty.slice";
import { updateRecentlyViewedData } from "../Corp/CorpUser.Slice";
import { fetchBookmarkedInncodeAsync, updateBookmarkedInncodesAsync } from "../dashboard/Dashboard.slice";
import BrandDeadlines from "./BrandDeadlines";
import { reset } from "./IndividualProperty.slice";
import Initiatives from "./Initiatives";
import Members from "./Members";
import PropertyPlans from "./PropertyPlans";
import SnapShot from "./SnapShot";

const Badge = ({ children }: any) => {
  return (
    <Typography
      variant="label"
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "100%",
        backgroundColor: tokens.Icon.Feeback.Alert,
        color: tokens.Text.General.Inverse,
        lineHeight: "20px"
      }}
    >
      {children}
    </Typography>
  );
};

const IndividualProperty = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { isMobileScreen } = useContext(ThemeContext);
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const { propertyList, bookmarkedProperties } = useAppSelector((state) => state.aboveProperty);
  const propertyPlans = useAppSelector((state) => state.aboveProperty.planProgress);
  const {
    propName = "",
    brandName = "",
    innCode = ""
  } = propertyList?.find((el: any) => el?.innCode?.toLowerCase() === params?.inncode?.toLowerCase()) || {};

  const tabDetails = [
    {
      label: "Property Plans",
      content: PropertyPlans,
      badge: propertyPlans?.meta?.overDueMyPlansCount ? <Badge>{propertyPlans?.meta?.overDueMyPlansCount}</Badge> : null
    },
    {
      label: "Brand Deadlines",
      content: BrandDeadlines,
      badge: propertyPlans?.meta?.overDueDeadlinePlansCount ? (
        <Badge>{propertyPlans?.meta?.overDueDeadlinePlansCount}</Badge>
      ) : null
    },
    { label: "Initiatives", content: Initiatives }
  ];
  const isBookmarked = bookmarkedProperties?.some((b: any) => b?.innCode === params?.inncode);

  useEffect(() => {
    if (isEmpty(bookmarkedProperties)) dispatch(fetchBookmarkedInncodeAsync({}));
    if (isAdmin) {
      dispatch(
        updateRecentlyViewedData({
          property: [
            {
              innCode: params?.inncode
            }
          ]
        })
      );
      dispatch(fetchPropertyInfo({ innCodesValue: params?.inncode }));
    }

    return () => {
      dispatch(reset());
      dispatch(resetAbovePropertySlice());
    };
  }, []);

  const handleBookmarking = async (isbookmark: boolean) => {
    if (isbookmark) {
      await dispatch(
        updateBookmarkedInncodesAsync({
          bookmarks: bookmarkedProperties?.filter((item: any) => item?.innCode !== params?.inncode)
        })
      );
    } else {
      await dispatch(
        updateBookmarkedInncodesAsync({
          bookmarks: [
            ...bookmarkedProperties,
            {
              innCode: params?.inncode
            }
          ]
        })
      );
    }
    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        variant: "success",
        setState: setFeedbackDetails({ shouldShow: false }),
        message: isbookmark ? "Property removed from bookmarked." : "Property added to bookmarked."
      })
    );
  };

  return (
    <>
      <Stack spacing={1} direction={isMobileScreen ? "column" : "row"} sx={{ mt: "24px" }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            showBold
            color={tokens.Text.General.PrimaryHeadline}
            sx={{
              mb: "16px"
            }}
          >
            {propName}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="body2"
              color={tokens.Text.General.PrimaryHeadline}
              sx={{ borderRight: `1px solid ${tokens.Border.General.Secondary}`, pr: "12px", mr: "12px" }}
            >
              {innCode}
            </Typography>
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
              {brandName}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, textAlign: isMobileScreen ? "center" : "right" }}>
          <Button
            label={
              <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
                {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                <Typography
                  showBold
                  variant={"body2"}
                  children={isBookmarked ? "Remove from Bookmarked" : "Add to Bookmarked"}
                ></Typography>
              </Box>
            }
            variant="secondary"
            sx={isMobileScreen ? { width: "100%", mt: "32px" } : {}}
            accessType={isImpersonated ? accessTypes.update : accessTypes.view}
            onClick={() => handleBookmarking(isBookmarked)}
          />
        </Box>
      </Stack>
      <SnapShot id={params?.inncode} />
      <Members />
      <Typography
        variant="h4"
        showBold
        color={tokens.Text.General.PrimaryHeadline}
        sx={{
          mt: "44px",
          mb: "20px"
        }}
      >
        Plans
      </Typography>
      <Basictabs tabDetails={tabDetails} alignItems="left" marginBottom="0" isParentTab />
    </>
  );
};

export default IndividualProperty;
